import axios from 'axios';
import { get, uniq } from 'lodash';
import Vue from 'vue';
import VueAxios from 'vue-axios';

import { makeToastFaile } from '@/utils/common';
import { ERROR_CODE } from '@/utils/enum';
import { getData } from '@/utils/saveDataToLocal';

import { getServer } from '@/core/config/server';
import JwtService from '@/core/services/jwt.service';
import { LOGOUT } from '@/core/services/store/modules/auth.module';

import store from '@/core/services/store';
import router from '@/router';

/**
 * Service to call HTTP request via Axios
 */
let errorMessages = [];

const server = getServer({
  rootApiConfig: process.env.ROOT_API_CONFIG || 'development',
  version: process.env.VUE_APP_VERSION || '2.0.0',
});
const READ_ONLY_BACKEND = server.read;
const READ_AND_WRITE_BACKEND = server.write;

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = READ_ONLY_BACKEND;

    this.addInterceptors(Vue.axios);
  },
  addInterceptors(instance) {
    instance.interceptors.request.use(
      this.reqInterceptor,
      this.reqErrorInterceptor,
    );

    instance.interceptors.response.use(
      this.resInterceptor,
      this.resErrorInterceptor,
    );
  },

  reqInterceptor(config) {
    config.headers['Authorization'] = `Bearer ${JwtService.getToken()}`;
    config.headers['version'] = server.version;
    config.headers['company-id'] = parseInt(getData('currentCompanyId')) || 1;

    return config;
  },

  reqErrorInterceptor(error) {
    return Promise.reject(error);
  },

  resInterceptor(response) {
    return response;
  },

  resErrorInterceptor(error) {
    const errorCode = get(error, 'response.status');
    switch (errorCode) {
      case ERROR_CODE.UNAUTHORIZED: {
        store.dispatch(LOGOUT);
        router.push({ name: 'login' });
        return Promise.reject(error);
      }
      case ERROR_CODE.ERROR_VERSION: {
        const message = get(error, 'response.data.message');
        errorMessages.push(message);
        setTimeout(() => {
          errorMessages = uniq(errorMessages);
          errorMessages.map((message) => {
            makeToastFaile(message);
          });
          errorMessages = [];
        }, 2000);
        return Promise.reject(error);
      }
      default:
        return Promise.reject(error);
    }
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    Vue.axios.defaults.headers.common['Authorization'] =
      `Bearer ${JwtService.getToken()}`;
  },

  query(resource, params) {
    return Vue.axios.get(resource, params);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param config - Axios config object (params, headers, etc.)
   * @returns {*}
   */
  get(resource, config = {}) {
    return Vue.axios.get(resource, config);
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @param idempotentKey
   * @returns {*}
   */
  post(resource, params, idempotentKey) {
    const headers = {
      'x-idempotence-key': idempotentKey,
    };
    return Vue.axios.post(`${resource}`, params, {
      baseURL: READ_AND_WRITE_BACKEND,
      headers,
    });
  },

  /**
   * Set the PATCH HTTP request
   * @param resource
   * @returns {*}
   */
  patch(resource, params) {
    return Vue.axios.patch(`${resource}`, params);
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params, {
      baseURL: READ_AND_WRITE_BACKEND,
    });
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @param idempotentKey
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params, idempotentKey) {
    const headers = {
      'x-idempotence-key': idempotentKey,
    };
    return Vue.axios.put(`${resource}`, params, {
      baseURL: READ_AND_WRITE_BACKEND,
      headers,
    });
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return Vue.axios
      .delete(resource, {
        baseURL: READ_AND_WRITE_BACKEND,
      })
      .catch((error) => {
        if (error.response) {
          makeToastFaile(
            error.response.data
              ? error.response.data.message
              : 'Có vấn đề xảy ra hãy thử lại',
          );
        } else {
          makeToastFaile(error.message || 'Có vấn đề xảy ra hãy thử lại');
        }
        return error;
      });
  },

  /**
   * Send the POST HTTP request
   * @param resource
   * @returns {*}
   */
  download(url, body, options) {
    return axios.post(url, body, options).catch((error) => {
      if (error.response) {
        makeToastFaile(
          error.response.data
            ? error.response.data.message
            : 'Có vấn đề xảy ra hãy thử lại',
        );
      } else {
        makeToastFaile(error.message || 'Có vấn đề xảy ra hãy thử lại');
      }
      return error;
    });
  },

  getServerInformation() {
    return server;
  },
};

export default ApiService;
